import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { UserPlus } from 'lucide-react'
import { AuthShell } from './AuthShell'
import { Button, ErrorNote, Field, Input } from '@/components/ui'
import { useAuth } from '@/lib/auth'
import { authErrorMessage } from '@/lib/authErrors'

const MIN_PASSWORD_LENGTH = 8

// Bara formen: något, @, något med en punkt. Resten avgör Firebase.
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function SignUpPage() {
  const { signUp } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Kommer man hit från aktiveringssidan följer koden med, så att man
  // hamnar rätt igen direkt efter att kontot skapats.
  const pendingCode = searchParams.get('kod')

  const [email, setEmail] = useState(searchParams.get('mail') ?? '')
  const [password, setPassword] = useState('')
  const [repeat, setRepeat] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /*
   * Adressen bekräftas innan kontot skapas.
   *
   * Ingen adress verifieras med mejl, och det är medvetet: det skulle stoppa
   * en förälder i kassakön som vill använda en rabatt nu. Men då finns heller
   * ingen som fångar en felskrivning. Den som skriver "gmial.com" får aldrig
   * något återställningsmejl, och koden som aktiveras hör till det kontot för
   * gott. Ett ögonblick att läsa sin adress är billigt mot det.
   */
  const [confirming, setConfirming] = useState(false)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)

    if (!EMAIL_SHAPE.test(email.trim())) {
      setError('Det ser inte ut som en e-postadress. Kontrollera att den har ett @ och en punkt.')
      return
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Lösenordet måste vara minst ${MIN_PASSWORD_LENGTH} tecken.`)
      return
    }
    if (password !== repeat) {
      setError('Lösenorden är inte lika. Kontrollera att du skrivit samma två gånger.')
      return
    }
    if (!confirming) {
      setConfirming(true)
      return
    }

    setBusy(true)
    try {
      await signUp(email, password)
      navigate(pendingCode ? `/aktivera?kod=${encodeURIComponent(pendingCode)}` : '/', {
        replace: true,
      })
    } catch (caught) {
      setError(authErrorMessage(caught, 'signUp'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthShell
      title="Skapa konto"
      showValue
      intro="Du behöver bara en e-postadress och ett lösenord. Vi frågar inte efter namn, adress eller telefonnummer."
      footer={
        <>
          Har du redan ett konto?{' '}
          <Link to="/logga-in" className="font-semibold text-accent underline underline-offset-2">
            Logga in
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {error && <ErrorNote>{error}</ErrorNote>}

        <Field label="E-post" required>
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              required
              value={email}
              onChange={(event) => {
                setEmail(event.target.value)
                // En ändrad adress är inte längre den som bekräftades.
                setConfirming(false)
              }}
              placeholder="namn@exempel.se"
            />
          )}
        </Field>

        <Field label="Lösenord" hint={`Minst ${MIN_PASSWORD_LENGTH} tecken.`} required>
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              type="password"
              autoComplete="new-password"
              required
              minLength={MIN_PASSWORD_LENGTH}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          )}
        </Field>

        <Field label="Upprepa lösenordet" required>
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              type="password"
              autoComplete="new-password"
              required
              value={repeat}
              onChange={(event) => setRepeat(event.target.value)}
            />
          )}
        </Field>

        {confirming ? (
          <div
            role="group"
            aria-labelledby="bekrafta-adress"
            className="space-y-3 rounded-card border-2 border-brand/30 bg-brand-soft p-4"
          >
            <p id="bekrafta-adress" className="text-sm font-semibold text-ink">
              Stämmer adressen?
            </p>
            <p className="text-lg font-bold break-all text-brand-ink">{email.trim()}</p>
            <p className="text-sm text-muted">
              Glömmer du lösenordet skickar vi länken hit. Är adressen fel kan du inte själv få
              tillbaka kontot – och inte heller häftet du aktiverar på det.
            </p>
            <Button type="submit" size="lg" fullWidth loading={busy} icon={<UserPlus size={18} />}>
              Ja, skapa kontot
            </Button>
            <Button type="button" variant="ghost" fullWidth onClick={() => setConfirming(false)}>
              Ändra adressen
            </Button>
          </div>
        ) : (
          <Button type="submit" size="lg" fullWidth loading={busy} icon={<UserPlus size={18} />}>
            Skapa konto
          </Button>
        )}

        {/* Länken står här och inte i en fotnot: det är i det ögonblick man
            lämnar ifrån sig sin adress som man har anledning att läsa vad
            som händer med den. */}
        <p className="text-center text-xs text-muted text-balance">
          Vi sparar bara din e-postadress och vilka rabatter du använt. Du kan när som helst
          radera kontot under Mitt konto.{' '}
          <Link to="/integritet" className="font-semibold text-brand-ink underline">
            Så hanterar vi dina uppgifter
          </Link>
          .
        </p>
      </form>
    </AuthShell>
  )
}
